import React, { useRef, useLayoutEffect, useState } from "react";
import cx from 'clsx';

import { FieldWrapper } from "@montana/shared/form/field-wrapper";

import { withForm } from "../field";
import { isFieldTouched, isFieldValid } from "../field/field.component";
import { ISharedFieldProperties } from "../field/field";
import { useTranslations } from "@montana/containers/translations";
import { isBrowser } from "@montana/helpers/browser.helper";


type ITextInputProps = React.InputHTMLAttributes<HTMLInputElement> & ISharedFieldProperties;

/**
 * @author SBF & AGI
 *
 * @description
 * Can be used otuside of a <Form>, state must be handled manually.
 *
 * @example
 *  <TextInput type="text" name="some-checkbox" onChange={setValue} value={value}>
 *      Label text...
 *  </TextInput>
 */
export const TextInput: React.FC<ITextInputProps> = React.memo((props: ITextInputProps): JSX.Element => {
    const {
        className = "",
        isValid = true,
        label,
        errorMessage,
        isTouched,
        inlineLabel = true,
        floatingLabel,
        autoFocus,
        autoFocusDelay,
        required,
        optionalIndicator = true,
        children,
        onFocus,
        onBlur,
        ...inputprops
    } = props;
    const { value, id, type } = props;

    const inputElement = useRef<HTMLInputElement | null>(null);

    const [inFocus, setInFocus] = useState(false);

    const translate = useTranslations();

    const showOptionalIndicator = optionalIndicator && required !== true;
    const showExtra = children || showOptionalIndicator;

    const useDynamicEffect = isBrowser ? useLayoutEffect : (): void => {/* Do nothing. */};

    useDynamicEffect(() => {
        if (autoFocus) {
            if (autoFocusDelay) {
                setTimeout(() => inputElement.current && inputElement.current.focus(), autoFocusDelay);
            } else {
                inputElement.current && inputElement.current.focus();
            }
        }
    }, []);

    const onClickWrapper = () => {
        if (inputElement.current) {
            inputElement.current.focus();
        }
    };

    return (
        <FieldWrapper
            label={label || ""}
            id={id || ""}
            inFocus={inFocus}
            isValid={isValid}
            isTouched={isTouched}
            isEmpty={!value}
            errorMessage={errorMessage}
            typeName={type || "text"}
            inlineLabel={inlineLabel}
            onClick={onClickWrapper}
        >
            <>
                <input
                    ref={inputElement}
                    className={cx('text-input', 'text-input--single-line', `text-input--${type || "text"}`, (className || ''), {
                        'text-input--basic': inlineLabel,
                        'text-input--inline-label': inlineLabel
                    })}
                    onFocus={(event) => {
                        setInFocus(true);
                        if (typeof onFocus === 'function') {
                            onFocus(event);
                        }
                    }}
                    onBlur={(event) => {
                        setInFocus(false);
                        if (typeof onBlur === 'function') {
                            onBlur(event);
                        }
                    }}
                    {...inputprops}
                />
                { showExtra &&
                    <div className="text-input__extra">
                        {showOptionalIndicator &&
                            <div className="text-input__optional">
                                {translate(t => t.forms.optional, '[Optional]')}
                            </div>
                        }
                        {children}
                    </div>
                }
            </>
        </FieldWrapper>
    );
});

/**
 * @author SBF
 *
 * @description
 * Must be used in the context of a <Form>
 *
 * @example
 *  <Form>
 *      <FormTextInput type="text" name="some-input" validate={required("Field required")} />
 *  </Form>
 */
export const FormTextInput = withForm<
ITextInputProps,
'value' | 'onChange' | 'isValid' | "isTouched" | "errorMessage"
>(TextInput, (field, form): ITextInputProps => ({
    onChange: form.handleChange,
    onBlur: form.handleBlur,
    value: field.value,
    errorMessage: (form.errors as { [key: string ]: string })[field.name],
    isValid: isFieldValid(field.name, form),
    isTouched: isFieldTouched(field.name, form),
}));
